import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button, Select, Space } from 'antd';
import { updateUser } from '../../redux/modules/users';
import EditionSelector from '../EditionSelector/EditionSelector';
import { isSpoofSessionActive, isSpoofActorSuperAdmin } from '../../services/spoofSession';
import { buildContextUrl, parseUrlContext, resolveContextSwitchPath } from '../../routing/contextUrl';

import './InstanceSelector.css';

const InstanceSelector = ({ collapsed, onCollapseNavClick }) => {
    const current_user                  = useSelector(state => state.auth.user);
    const tkt_instances                 = useSelector(state => state.tktInstances.tktInstances);
    const available_editions            = useSelector(state => state.editionNavigation?.editions) || [];
    const dispatch                      = useDispatch();

    const membership_ids = useMemo(() => {
        if (!current_user) {
            return [];
        }
        const raw = current_user.tkt_instance_ids;
        if (Array.isArray(raw) && raw.length > 0) {
            return raw;
        }
        if (current_user.tkt_instance_id) {
            return [current_user.tkt_instance_id];
        }
        return [];
    }, [current_user]);

    const select_options = useMemo(() => {
        if (!current_user || !tkt_instances) {
            return [];
        }
        const active = tkt_instances.filter(tkt_instance => tkt_instance.is_active);
        if (current_user.isSuperAdmin()) {
            return active.map(tkt_instance => ({
                label: tkt_instance.name,
                value: tkt_instance.id,
            }));
        }
        const allowed = new Set(membership_ids);
        return active
            .filter(tkt_instance => allowed.has(tkt_instance.id))
            .map(tkt_instance => ({
                label: tkt_instance.name,
                value: tkt_instance.id,
            }));
    }, [current_user, tkt_instances, membership_ids]);

    const show_instance_dropdown =
        select_options.length > 0 &&
        (current_user?.isSuperAdmin() || membership_ids.length > 1);

    const can_list_editions = Boolean(
        current_user?.abilities?.superadmin
        || (typeof current_user?.can === 'function' && current_user.can('read', 'editions'))
    );

    if (!current_user) {
        return null;
    }

    // Spoof sessions hide instance switching for non-superadmin actors (or single-membership),
    // but edition navigation must remain available when the user can list editions.
    const hide_instance_ui = isSpoofSessionActive()
        && (!isSpoofActorSuperAdmin() || membership_ids.length <= 1);

    const may_use_instance_selector =
        current_user.isSuperAdmin() ||
        current_user.can('edit_templates', 'users') ||
        current_user.can('editSelf', 'tktInstances') ||
        membership_ids.length > 1;

    const show_instance_block = Boolean(
        may_use_instance_selector && !hide_instance_ui && tkt_instances
    );

    // Same visibility rule as EditionSelector: only when there is a real choice.
    const show_edition_block = can_list_editions && available_editions.length > 1;

    // EditionSelector used to be unreachable whenever InstanceSelector early-returned
    // (no instance rights / spoof). Keep edition switching independent of instance UI.
    if (!show_instance_block && !show_edition_block) {
        return null;
    }

    const on_instance_change = (value) => {
        // Resolve the target slug so the new instance is reflected in the URL
        // prefix; otherwise ContextGate would see a stale slug after reload and
        // try to switch back. Fall back to the slug returned by the API.
        const from_list = (tkt_instances || []).find(i => i.id === value)?.slug;
        dispatch(
            updateUser(
                {
                    id: current_user.id,
                    tkt_instance_id: value,
                },
                { switching_tkt_instance: true }
            )
        )
            .then((data) => {
                const new_slug = from_list || data?.user?.tkt_instance?.slug || null;
                if (new_slug) {
                    // Prefer staying on the current collection (or its parent when the
                    // path holds a document id); deep ids are instance-scoped.
                    const logical = parseUrlContext(window.location.pathname).logicalPath;
                    const target_path = resolveContextSwitchPath(logical, '/');
                    const same_page = target_path === logical;
                    window.location.assign(
                        buildContextUrl(
                            { slug: new_slug, editionId: null },
                            target_path,
                            same_page ? (window.location.search || '') : '',
                            same_page ? (window.location.hash || '') : ''
                        )
                    );
                } else {
                    window.location.reload();
                }
            })
            .catch(() => {
                /* updateUser already dispatched failure; avoid reload */
            });
    };

    const gear_to = current_user.isSuperAdmin()
        ? '/tktInstances'
        : `/tktInstances/edit/${current_user.tkt_instance_id}`;

    return (
        <div className={`InstanceSelector${collapsed ? ' InstanceSelector--collapsed' : ''}`}>
            <div className="InstanceSelector__row">
                {show_instance_block && (
                    <Space.Compact block className="InstanceSelector__compact">
                        {(!collapsed && !show_instance_dropdown && current_user.tkt_instance?.name) &&
                            <div className="pad-instance InstanceSelector__instanceName">
                                <i className="fa fa-ticket"></i>&nbsp;
                                {current_user.tkt_instance.name}
                            </div>
                        }
                        {(!collapsed && show_instance_dropdown) &&
                            <Select
                                key={String(current_user.tkt_instance_id)}
                                showSearch
                                optionFilterProp="label"
                                options={select_options}
                                value={current_user.tkt_instance_id}
                                onChange={on_instance_change}
                                className="InstanceSelector__select"
                            />
                        }
                        <Link onClick={onCollapseNavClick} to={gear_to} className={`${collapsed ? "w-100" : ""}`} title={current_user.tkt_instance?.name}>
                            <Button className={`InstanceSelector__gear ${collapsed ? "custom-btn-width-collapsed" : ""}`} type="default">
                                <i className="fa fa-gear"></i>
                            </Button>
                        </Link>
                    </Space.Compact>
                )}
                {show_edition_block && <EditionSelector collapsed={collapsed} />}
            </div>
        </div>
    );
}

export default InstanceSelector;
